import React, { Fragment, useContext, useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Users, Briefcase, Settings, LogOut, CheckSquare, List, Shield, Layers, Package, Mail, ChevronDown, ChevronUp, LifeBuoy } from "react-feather";
import { AuthContext } from "../../../Auth/AuthContext";
import AxiosInstance from "../../../services/AxiosInstance";

/**
 * Map module_id (from API) to path and icon for sidebar.
 * Must match nf_module.module_id in database.
 */
const MODULE_MENU_MAP = {
    "1": { path: "/job/admin/dashboard", title: "Dashboard", icon: <Home size={18} /> },
    "2": { path: "/job/admin/manage-jobs", title: "Manage Jobs", icon: <Briefcase size={18} /> },
    "3": { path: "/job/admin/manage-recruiters", title: "Recruiters", icon: <Users size={18} /> },
    "4": { path: "/job/admin/manage-jobseekers", title: "Jobseekers", icon: <CheckSquare size={18} /> },
    "9": { path: "/job/admin/support-tickets", title: "Support Tickets", icon: <LifeBuoy size={18} /> },
    "10": { path: "/job/admin/invitation-campaign", title: "Invitation Campaigns", icon: <Mail size={18} /> },
    // "5": { path: "/job/admin/categories", title: "Categories", icon: <List size={18} /> },
    // "6": { path: "/job/admin/settings", title: "Settings", icon: <Settings size={18} /> },
};

const SidebarMenu = () => {
    const { logout } = useContext(AuthContext);
    const location = useLocation();
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submenuOpen, setSubmenuOpen] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState("");

    const toggleSubmenu = (key) => {
        if (submenuOpen && activeSubmenu === key) {
            setSubmenuOpen(false);
            setActiveSubmenu("");
        } else {
            setSubmenuOpen(true);
            setActiveSubmenu(key);
        }
    };

    useEffect(() => {
        let cancelled = false;
        const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
        const isSuper = userData.is_super === 1 || userData.is_super === "1" || userData.is_super === undefined;

        const load = async () => {
            try {
                const res = await AxiosInstance().post("/admin/role/my_permissions", {});
                if (cancelled) return;
                const list = [];
                if (res.data.status && Array.isArray(res.data.data)) {
                    res.data.data.forEach((p) => {
                        if (!(p.operations || []).includes("access")) return;
                        const modId = String(p.module);
                        const config = MODULE_MENU_MAP[modId];
                        const path = config ? config.path : `/job/admin/module/${modId}`;
                        const title = p.module_name || (config ? config.title : `Module ${modId}`);
                        const icon = config?.icon ?? (p.fa_icon ? <i className={`fa ${p.fa_icon}`} style={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center" }} /> : <List size={18} />);
                        list.push({ path, title, icon });
                    });
                }
                if (isSuper) {
                    list.push({
                        type: "dropdown",
                        key: "company",
                        title: "Company",
                        icon: <Package size={18} />,
                        children: [
                            { path: "/job/admin/manage-companies", title: "View Companies" },
                            { path: "/job/admin/add-company", title: "Add Company" },
                        ],
                    });
                    list.push({
                        type: "dropdown",
                        key: "users",
                        title: "Manage Users",
                        icon: <Users size={18} />,
                        children: [
                            { path: "/job/admin/manage-users", title: "View Users" },
                            { path: "/job/admin/add-user", title: "Add User" },
                        ],
                    });
                    list.push({
                        type: "dropdown",
                        key: "roles",
                        title: "Manage Roles",
                        icon: <Shield size={18} />,
                        children: [
                            { path: "/job/admin/manage-roles", title: "View Roles" },
                            { path: "/job/admin/manage-roles/new", title: "Add Role" },
                        ],
                    });
                    list.push({
                        type: "dropdown",
                        key: "modules",
                        title: "Manage Modules",
                        icon: <Layers size={18} />,
                        children: [
                            { path: "/job/admin/manage-modules", title: "View Modules" },
                            { path: "/job/admin/manage-modules/new", title: "Add Module" },
                        ],
                    });
                }
                setMenuItems(list);
            } catch (e) {
                if (!cancelled) {
                    setMenuItems(
                        Object.entries(MODULE_MENU_MAP).map(([_, c]) => ({
                            path: c.path,
                            title: c.title,
                            icon: c.icon,
                        }))
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, []);

    const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

    return (
        <nav className="sidebar-main mt-4">
            <div className="left-arrow" id="left-arrow"><i data-feather="arrow-left"></i></div>
            <div id="sidebar-menu">
                <ul className="sidebar-links custom-scrollbar list-unstyled px-3">
                    <li className="sidebar-main-title mb-2">
                        <div className="ps-2">
                            <h6 className="text-uppercase text-muted small fw-bold mb-0">General</h6>
                        </div>
                    </li>
                    {!loading && menuItems.map((item, index) => (
                        item.type === "dropdown" ? (
                            <li className="sidebar-list mb-1" key={item.key || index}>
                                <button
                                    type="button"
                                    className={`sidebar-link sidebar-title link-nav d-flex align-items-center p-2 rounded w-100 border-0 bg-transparent text-start ${submenuOpen && activeSubmenu === item.key ? "active bg-primary-subtle text-primary" : "text-dark hover-bg-light"}`}
                                    style={{ transition: "all 0.2s ease", cursor: "pointer" }}
                                    onClick={() => toggleSubmenu(item.key)}
                                >
                                    <span className="me-3 d-flex align-items-center">{item.icon}</span>
                                    <span className="fw-medium flex-grow-1">{item.title}</span>
                                    {submenuOpen && activeSubmenu === item.key ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>
                                {submenuOpen && activeSubmenu === item.key && (
                                    <ul className="sidebar-submenu list-unstyled mb-0 ps-0">
                                        {(item.children || []).map((child, i) => (
                                            <li key={i}>
                                                <NavLink
                                                    to={child.path}
                                                    className={({ isActive }) =>
                                                        `sidebar-sublink d-block px-2 py-2 ps-4 rounded small ${isActive || isPathActive(child.path) ? "active text-primary fw-medium" : "text-dark"}`
                                                    }
                                                    style={{ textDecoration: "none" }}
                                                >
                                                    {child.title}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ) : (
                            <li className="sidebar-list mb-1" key={index}>
                                <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                        `sidebar-link sidebar-title link-nav d-flex align-items-center p-2 rounded ${isActive ? "active bg-primary-subtle text-primary" : "text-dark hover-bg-light"}`
                                    }
                                    style={{ transition: "all 0.2s ease" }}
                                >
                                    <span className="me-3 d-flex align-items-center">{item.icon}</span>
                                    <span className="fw-medium">{item.title}</span>
                                </NavLink>
                            </li>
                        )
                    ))}
                    {loading && (
                        <li className="sidebar-list mb-1">
                            <span className="sidebar-link d-flex align-items-center p-2 rounded text-muted">
                                <span className="me-3">...</span>
                                <span>Loading...</span>
                            </span>
                        </li>
                    )}

                    <li className="sidebar-main-title mt-4 mb-2">
                        <div className="ps-2">
                            <h6 className="text-uppercase text-muted small fw-bold mb-0">System</h6>
                        </div>
                    </li>
                    <li className="sidebar-list">
                        <a
                            href="#"
                            className="sidebar-link sidebar-title link-nav d-flex align-items-center p-2 rounded text-danger hover-bg-danger-subtle"
                            onClick={(e) => { e.preventDefault(); logout(); }}
                            style={{ transition: "all 0.2s ease" }}
                        >
                            <span className="me-3 d-flex align-items-center"><LogOut size={18} /></span>
                            <span className="fw-medium">Logout</span>
                        </a>
                    </li>
                </ul>
            </div>
            <div className="right-arrow" id="right-arrow"><i data-feather="arrow-right"></i></div>

            <style jsx>{`
                .hover-bg-light:hover { background-color: #f8f9fa; }
                .hover-bg-danger-subtle:hover { background-color: rgba(220, 53, 69, 0.1); }
                .sidebar-link.active {
                    color: #0b5ed7 !important;
                    background-color: rgba(13, 110, 253, 0.1);
                }
                .sidebar-sublink:hover { background-color: rgba(0,0,0,0.04); }
                .sidebar-links {
                    overflow-y: auto;
                    max-height: calc(100vh - 150px);
                }
            `}</style>
        </nav>
    );
};

export default SidebarMenu;
